import { Field, ImageField } from '@sitecore-jss/sitecore-jss-nextjs';
import Accordion from '../../components/shared/Accordion/Accordion';
import IconWithTitle from '../../components/shared/IconWithTitle/IconWithTitle';
import { ComponentProps } from '../../lib/component-props';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import CollapseExpandIcon from '../../components/shared/Accordion/CollapseExpandIcon';
import {
  StDTPGridContainer,
  StDTPWrapper,
} from '../../components/shared/Container/Container.styles';
import Head from 'next/head';

type Accordion = {
  fields: {
    accordionTitle: Field<string>;
    accordionDescription: Field<string>;
  };
};

type AccordionPanelProps = ComponentProps & {
  fields: {
    title: Field<string>;
    titleIcon: ImageField;
    iconWithTitleVariant: { value: string };
    hideExpandAllButton: Field<boolean>;
    expandOnlyOneAccordion: Field<boolean>;
    expandAllText: Field<string>;
    collapseAllText: Field<string>;
    sectionsList: Accordion[];
    ariaLabel: Field<string>;
  };
};

const AccordionPanel = (props: AccordionPanelProps): JSX.Element => {
  const theme = useTheme();
  const isSmallerDevice = useMediaQuery(theme.breakpoints.down(600));

  if (!props?.fields) return <></>;
  const IconWithTitleProps = {
    titleIcon: props?.fields?.titleIcon,
    title: props?.fields?.title,
    iconWithTitleVariant: props?.fields?.iconWithTitleVariant?.value
      ? props?.fields?.iconWithTitleVariant?.value
      : 'ImageUp',
  };
  const accordionProps = {
    expandOnlyOneAccordion: props?.fields?.expandOnlyOneAccordion
      ? props?.fields?.expandOnlyOneAccordion?.value
      : false,
    hideExpandAllButton: props?.fields?.hideExpandAllButton
      ? props?.fields?.hideExpandAllButton?.value
      : false,
    sectionsList: props.fields?.sectionsList,
    expandAllText: props.fields?.expandAllText?.value,
    collapseAllText: props.fields?.collapseAllText?.value,
    headingWithIconVariant: props?.fields?.iconWithTitleVariant
      ? props?.fields?.iconWithTitleVariant?.value
      : 'ImageUp',
  };
  const generateFAQSchema = () => {
    if (!props?.fields?.sectionsList || props?.fields?.sectionsList?.length === 0) {
      return null;
    }

    const faqSchema = {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: props?.fields?.sectionsList?.map((item) => ({
        '@type': 'Question',
        name: item?.fields?.accordionTitle?.value || '',
        acceptedAnswer: {
          '@type': 'Answer',
          text: `${item?.fields?.accordionDescription?.value || ''}${
            item?.fields?.accordionTitle?.value
              ? ` ${item?.fields?.accordionDescription?.value}`
              : ''
          }`
            .replace(/<[^>]*>/g, '')
            .trim(),
        },
      })),
    };

    return (
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(faqSchema),
          }}
        />
      </Head>
    );
  };
  return (
    <Box
      sx={{ background: theme.palette.backgroundColorType3.main }}
      id="faqs-main"
      className="ui-accordion-panel"
    >
      {generateFAQSchema()}
      <StDTPWrapper
        id={'faqs'}
        className="ui-accordion-panel__wrapper"
        bgColor={theme.palette.backgroundColorType3.main}
      >
        <StDTPGridContainer
          bgColor={theme.palette.backgroundColorType3.main}
          className="app-ui-text"
        >
          <Box sx={{ paddingBlock: '1.5rem' }}>
            <Accordion {...accordionProps}>
              {(singleLineHeading, handleExpand, showExpandIcon, showExpandIconText) => (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: accordionProps?.hideExpandAllButton
                      ? 'center'
                      : 'space-between',
                    alignItems: 'center',
                    paddingBottom: isSmallerDevice ? '0.5rem' : '1rem',
                  }}
                >
                  {accordionProps?.hideExpandAllButton ? (
                    <>
                      <IconWithTitle {...IconWithTitleProps} />
                    </>
                  ) : (
                    <>
                      {!isSmallerDevice && (
                        <Box sx={{ visibility: 'hidden' }}>
                          <CollapseExpandIcon
                            singleLineHeading={singleLineHeading}
                            handleClick={handleExpand}
                            showExpandIcon={showExpandIcon}
                            showExpandIconText={showExpandIconText}
                            ariaLabel={props?.fields?.ariaLabel?.value}
                          />
                        </Box>
                      )}
                      <IconWithTitle {...IconWithTitleProps} />
                      <CollapseExpandIcon
                        handleClick={handleExpand}
                        singleLineHeading={singleLineHeading}
                        showExpandIcon={showExpandIcon}
                        showExpandIconText={showExpandIconText}
                        ariaLabel={props?.fields?.ariaLabel?.value}
                      />
                    </>
                  )}
                </Box>
              )}
            </Accordion>
          </Box>
        </StDTPGridContainer>
      </StDTPWrapper>
    </Box>
  );
};

export default AccordionPanel;
